import { Layout } from "@/components/layout";
import { SEOHead } from "@/components/seo-head";
import { PLATFORM_PAGES } from "@/lib/content";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import { Link, useParams } from "wouter";

export default function PlatformPage() {
  const { slug } = useParams<{ slug: string }>();
  const pageData = PLATFORM_PAGES[slug as keyof typeof PLATFORM_PAGES];

  if (!pageData) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-32 text-center">
          <h1 className="text-h1 mb-4">Platform Feature Not Found</h1>
          <p className="text-body-lg text-slate-500">The feature you are looking for does not exist.</p>
        </div>
      </Layout>
    );
  }

  // Handle integrations page differently since it has 'categories' instead of 'capabilities'
  const isIntegrations = slug === "integrations";

  return (
    <Layout>
      <SEOHead title={pageData.seo.title} description={pageData.seo.description} />
      
      {/* Hero */}
      <section className="pt-24 pb-20 md:pt-32 md:pb-24 border-b border-border/50 bg-slate-50 dark:bg-navy/30 relative overflow-hidden">
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />
        <div className="container mx-auto px-4 md:px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl"
          >
            <div className="text-sm font-semibold text-indigo-600 dark:text-indigo-400 mb-4 tracking-wider uppercase">
              {pageData.eyebrow}
            </div>
            <h1 className="text-display mb-6 text-navy dark:text-white">
              {pageData.title}
            </h1>
            <p className="text-body-lg text-slate-600 dark:text-slate-300">
              {pageData.subtitle}
            </p>
            {/*
              Optional in-context cross-link surfaced on platform pages
              that opt in via `relatedLink` in content.ts (Task #584 —
              currently used by `contract-intelligence` to point at the
              new `/platform/ai` positioning page). Rendered as a low-key
              text link below the subtitle so it doesn't compete with
              the page's own CTAs.
            */}
            {"relatedLink" in pageData && pageData.relatedLink ? (
              <Link
                href={pageData.relatedLink.path}
                className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline underline-offset-4"
                data-testid={`platform-related-link-${slug}`}
              >
                {pageData.relatedLink.label}
                <ArrowRight className="h-3.5 w-3.5" />
              </Link>
            ) : null}
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-20 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="max-w-5xl mx-auto">
            {!isIntegrations ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12">
                {('capabilities' in pageData ? pageData.capabilities : []).map((cap, i) => (
                  <motion.div 
                    key={i}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: i * 0.05 }}
                    className="flex gap-4"
                  >
                    <CheckCircle2 className="h-5 w-5 text-cyan-500 shrink-0 mt-1" />
                    <div>
                      <h3 className="text-h4 mb-2 text-navy dark:text-white">{cap.title}</h3>
                      <p className="text-body text-slate-600 dark:text-slate-400">{cap.body}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-12">
                {('categories' in pageData ? pageData.categories : []).map((cat, i) => {
                  // Some categories carry an optional CTA so visitors can click
                  // through to a dedicated page (e.g. the Word add-in). The
                  // type is widened locally because not every category in the
                  // const tuple has the field.
                  const cta = (cat as { cta?: { label: string; path: string } }).cta;
                  return (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: i * 0.05 }}
                    >
                      <h3 className="text-h4 mb-4 text-navy dark:text-white border-b pb-2">{cat.title}</h3>
                      <ul className="space-y-3">
                        {cat.items.map((item, j) => (
                          <li key={j} className="flex items-center gap-3 text-sm font-medium text-slate-700 dark:text-slate-300">
                            <CheckCircle2 className="h-4 w-4 text-cyan-500 shrink-0" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                      {cta && (
                        <Link
                          href={cta.path}
                          className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-indigo-600 dark:text-indigo-400 hover:underline"
                          data-testid={`integration-cta-${cat.title.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {cta.label}
                          <ArrowRight className="h-3.5 w-3.5" />
                        </Link>
                      )}
                    </motion.div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
}
